import {
    DEFAULT_KERNEL_RUN_TIMEOUT_MS,
    KERNEL_RUN_CANCEL_REASON,
    KernelRunCancelledError,
    cancelledKernelRunResult,
    failedKernelRunResult,
    isKernelRunCancellation,
    kernelRunCancelReasonFromSignal,
    kernelRunOutcomeOf,
    normalizeKernelRunCancelReason,
    overloadedKernelRunResult,
    reportedKernelRunOutcomeOf,
    reportedKernelRunReasonCategoryOf,
} from "./kernelRunOutcome.js";
import {
    releaseKernelRunRealm,
    subscribeKernelRunRealmTeardown,
    terminateKernelRunRealm,
} from "./kernelRunRealmAdapter.js";

export const DEFAULT_CANVAS_RUN_CONCURRENCY = Number.POSITIVE_INFINITY;
export const MAX_CANVAS_ADMITTED_RUNS = 32;
export const MAX_CANVAS_RUN_TIMEOUT_MS = DEFAULT_KERNEL_RUN_TIMEOUT_MS;
export { DEFAULT_KERNEL_RUN_TIMEOUT_MS };

let _nextSchedulerId = 1;

function positiveConcurrency(value, fallback) {
    const n = Number(value);
    return n === Number.POSITIVE_INFINITY || (Number.isInteger(n) && n > 0)
        ? n
        : fallback;
}

function boundedPositiveNumber(value, fallback, maximum) {
    const n = Number(value);
    return Number.isFinite(n) && n > 0 ? Math.min(n, maximum) : fallback;
}

export class CanvasRunScheduler {
    constructor({
        concurrency = DEFAULT_CANVAS_RUN_CONCURRENCY,
        timeoutMs = DEFAULT_KERNEL_RUN_TIMEOUT_MS,
        terminateRealm = terminateKernelRunRealm,
        releaseRealm = releaseKernelRunRealm,
        onOutcome = null,
    } = {}) {
        this.concurrency = positiveConcurrency(
            concurrency,
            DEFAULT_CANVAS_RUN_CONCURRENCY,
        );
        this.timeoutMs = boundedPositiveNumber(
            timeoutMs,
            DEFAULT_KERNEL_RUN_TIMEOUT_MS,
            MAX_CANVAS_RUN_TIMEOUT_MS,
        );
        this._terminateRealm = typeof terminateRealm === "function" ? terminateRealm : terminateKernelRunRealm;
        this._releaseRealm = typeof releaseRealm === "function" ? releaseRealm : releaseKernelRunRealm;
        this._onOutcome = typeof onOutcome === "function" ? onOutcome : null;
        this._schedulerId = _nextSchedulerId++;
        this._nextRunId = 1;
        this._nextExecutionId = 1;
        this._currentExecutionId = null;
        this._queue = [];
        this._entries = new Map();
        this._active = 0;
        this._disposed = false;
        // Construction can happen during a React render that is later
        // discarded. Do not publish a global listener until the scheduler
        // actually admits work; otherwise an abandoned render stays reachable
        // forever through kernelRunRealmAdapter's listener set.
        this._unsubscribeRealm = null;
    }

    schedule(task, {
        id = "run",
        signal = null,
        timeoutMs = this.timeoutMs,
        onStateChange = null,
        onOutcome = null,
        metadata = null,
    } = {}) {
        if (typeof task !== "function") throw new TypeError("CanvasRunScheduler.schedule needs a task function");
        this._ensureRealmSubscription();
        if (this._entries.size === 0) {
            this._currentExecutionId =
                `canvas-execution:${this._schedulerId}:${this._nextExecutionId++}`;
        }
        const runId = `canvas-run:${this._schedulerId}:${this._nextRunId++}:${String(id)}`;
        const executionId = this._currentExecutionId;
        const submittedAt = Date.now();
        const normalizedTimeoutMs = boundedPositiveNumber(
            timeoutMs,
            this.timeoutMs,
            MAX_CANVAS_RUN_TIMEOUT_MS,
        );
        let resolvePromise;
        const promise = new Promise((resolve) => { resolvePromise = resolve; });
        const entry = {
            id,
            runId,
            executionId,
            task,
            externalSignal: signal,
            timeoutMs: normalizedTimeoutMs,
            submittedAt,
            deadlineAt: submittedAt + normalizedTimeoutMs,
            onStateChange: typeof onStateChange === "function" ? onStateChange : null,
            onOutcome: typeof onOutcome === "function" ? onOutcome : null,
            metadata,
            controller: new AbortController(),
            resolve: resolvePromise,
            promise,
            state: "queued",
            launched: false,
            settled: false,
            cancelReason: null,
            timer: null,
            removeExternalAbort: null,
            taskPromise: null,
        };
        const handle = {
            id,
            runId,
            executionId,
            promise,
            cancel: (reason = null) => {
                const fallback = entry.launched
                    ? KERNEL_RUN_CANCEL_REASON.USER_STOP
                    : KERNEL_RUN_CANCEL_REASON.QUEUED_CANCELLED;
                return this._cancelEntry(entry, reason || { code: fallback });
            },
            get state() { return entry.state; },
        };

        // Capacity is active + waiting, not merely queue length. A cancelling
        // active run continues to occupy admission until termination is
        // confirmed, so the scheduler can never hide runaway overlap behind
        // churn. Rejection is a typed refusal and is never observed as queued.
        if (!this._disposed && this._entries.size >= MAX_CANVAS_ADMITTED_RUNS) {
            this._finish(entry, overloadedKernelRunResult({
                limit: MAX_CANVAS_ADMITTED_RUNS,
                admitted: this._entries.size,
                runId,
            }));
            return handle;
        }

        this._entries.set(runId, entry);
        this._queue.push(entry);
        // The published-run limit is submission-through-completion. Arm the
        // deadline while the entry is admitted so time waiting in FIFO counts
        // just like setup/query/Python/render work inside the scheduled task.
        entry.timer = setTimeout(() => {
            this._cancelEntry(entry, {
                code: KERNEL_RUN_CANCEL_REASON.TIMEOUT,
                timeoutMs: entry.timeoutMs,
                message: `Run exceeded its ${Math.round(entry.timeoutMs / 1000)}s wall-clock limit.`,
            });
        }, entry.timeoutMs);
        this._emitState(entry, "queued");

        if (signal) {
            const onAbort = () => {
                const fallback = entry.launched
                    ? KERNEL_RUN_CANCEL_REASON.ABORTED
                    : KERNEL_RUN_CANCEL_REASON.QUEUED_CANCELLED;
                this._cancelEntry(entry, kernelRunCancelReasonFromSignal(signal, fallback));
            };
            if (signal.aborted) {
                onAbort();
            } else {
                signal.addEventListener("abort", onAbort, { once: true });
                entry.removeExternalAbort = () => signal.removeEventListener("abort", onAbort);
            }
        }

        if (this._disposed && !entry.settled) {
            this._cancelEntry(entry, { code: KERNEL_RUN_CANCEL_REASON.SCHEDULER_DISPOSED });
        } else {
            this._pump();
        }

        return handle;
    }

    cancel(runId, reason = null) {
        const entry = this._entries.get(runId);
        if (!entry) return false;
        const fallback = entry.launched
            ? KERNEL_RUN_CANCEL_REASON.USER_STOP
            : KERNEL_RUN_CANCEL_REASON.QUEUED_CANCELLED;
        return this._cancelEntry(entry, reason || { code: fallback });
    }

    cancelAll(reason = { code: KERNEL_RUN_CANCEL_REASON.USER_STOP }) {
        const entries = [...this._entries.values()];
        for (const entry of entries) this._cancelEntry(entry, reason);
        return entries.length;
    }

    dispose(reason = { code: KERNEL_RUN_CANCEL_REASON.SCHEDULER_DISPOSED }) {
        if (this._disposed) return;
        this._disposed = true;
        this.cancelAll(reason);
        this._unsubscribeRealm?.();
        this._unsubscribeRealm = null;
    }

    snapshot() {
        return {
            active: this._active,
            queued: this._queue.filter((entry) => !entry.settled).length,
            total: this._entries.size,
            concurrency: this.concurrency,
            maxAdmittedRuns: MAX_CANVAS_ADMITTED_RUNS,
        };
    }

    _ensureRealmSubscription() {
        if (this._disposed || this._unsubscribeRealm) return;
        this._unsubscribeRealm = subscribeKernelRunRealmTeardown((event) => {
            this._handleRealmTeardown(event);
        });
    }

    _pump() {
        if (this._disposed) return;
        while (this._active < this.concurrency && this._queue.length) {
            const entry = this._queue.shift();
            if (!entry || entry.settled || entry.cancelReason) continue;
            this._launch(entry);
        }
    }

    _launch(entry) {
        entry.launched = true;
        this._active += 1;
        this._emitState(entry, "running", {
            submittedAt: entry.submittedAt,
            deadlineAt: entry.deadlineAt,
            startedAt: Date.now(),
        });

        entry.taskPromise = Promise.resolve().then(() => {
            if (entry.cancelReason) {
                return cancelledKernelRunResult(entry.cancelReason, { runId: entry.runId });
            }
            return entry.task({
                signal: entry.controller.signal,
                runId: entry.runId,
                executionId: entry.executionId,
                timeoutMs: entry.timeoutMs,
                submittedAt: entry.submittedAt,
                deadlineAt: entry.deadlineAt,
                metadata: entry.metadata,
            });
        });
        entry.taskPromise.then(
            (result) => {
                if (entry.cancelReason || entry.settled) return;
                this._finish(entry, result);
            },
            (error) => {
                if (entry.cancelReason || entry.settled) return;
                if (isKernelRunCancellation(error, entry.controller.signal)) {
                    const reason = kernelRunCancelReasonFromSignal(
                        entry.controller.signal,
                        KERNEL_RUN_CANCEL_REASON.ABORTED,
                    ) || normalizeKernelRunCancelReason(error);
                    this._finish(entry, cancelledKernelRunResult(reason, { error, runId: entry.runId }));
                    return;
                }
                this._finish(entry, failedKernelRunResult(error, { runId: entry.runId }));
            },
        );
    }

    _cancelEntry(entry, reason, { terminateRealm = true } = {}) {
        if (!entry || entry.settled || entry.cancelReason) return false;
        const fallback = entry.launched
            ? KERNEL_RUN_CANCEL_REASON.ABORTED
            : KERNEL_RUN_CANCEL_REASON.QUEUED_CANCELLED;
        const normalized = normalizeKernelRunCancelReason(reason, fallback);
        entry.cancelReason = normalized;
        if (!entry.launched) {
            this._queue = this._queue.filter((queued) => queued !== entry);
            this._finish(entry, cancelledKernelRunResult(normalized, { runId: entry.runId }));
            return true;
        }

        if (entry.timer) {
            clearTimeout(entry.timer);
            entry.timer = null;
        }
        this._emitState(entry, "cancelling", { reason: normalized });

        let teardown;
        try {
            // Capture/terminate the realm before AbortSignal listeners run.
            // The realm client removes its in-flight record synchronously on
            // abort; reversing this order loses the target and every sibling
            // id, misclassifying collateral cancellation as a failure.
            teardown = terminateRealm
                ? this._terminateRealm({
                    runId: entry.runId,
                    runIds: [entry.runId],
                    executionId: entry.executionId,
                    reason: normalized,
                    metadata: entry.metadata,
                })
                // `terminateRealm: false` is used only when the realm adapter
                // already reported this run as collateral damage.
                : { terminated: true };
        } catch (error) {
            teardown = { terminated: false, error };
        }
        try { entry.controller.abort(new KernelRunCancelledError(normalized)); } catch { entry.controller.abort(); }
        // A terminated realm is what interrupts synchronous Python. Once the
        // adapter has settled, the slot is safe to reuse; the abandoned task's
        // eventual resolution is ignored.
        Promise.resolve(teardown).catch(() => null).then((teardownResult) => {
            if (teardownResult?.terminated === true) {
                this._finish(entry, cancelledKernelRunResult(normalized, { runId: entry.runId }));
                return;
            }
            // A missing/failed realm adapter cannot prove synchronous Python
            // stopped. Keep the concurrency slot occupied until the task
            // itself observes AbortSignal and settles; otherwise a timed-out
            // runaway could keep burning while queued work starts behind it.
            // The extra microtask covers cancellation fired from the
            // synchronous "running" observer before _launch assigns taskPromise.
            Promise.resolve().then(() => entry.taskPromise).then(
                () => {
                    this._finish(entry, cancelledKernelRunResult(normalized, { runId: entry.runId }));
                },
                (error) => {
                    this._finish(entry, cancelledKernelRunResult(normalized, {
                        error,
                        runId: entry.runId,
                    }));
                },
            );
        });
        return true;
    }

    _handleRealmTeardown(event) {
        const ids = new Set(Array.isArray(event?.cancelledRunIds) ? event.cancelledRunIds : []);
        if (!ids.size && event?.all !== true) return;
        const affectedExecutionIds = new Set();
        for (const entry of this._entries.values()) {
            if (
                event?.all === true
                || ids.has(entry.runId)
                || event?.triggerRunId === entry.runId
                || event?.executionId === entry.executionId
            ) {
                affectedExecutionIds.add(entry.executionId);
            }
        }
        for (const entry of [...this._entries.values()]) {
            if (entry.settled || entry.cancelReason) continue;
            if (
                event.all !== true
                && !affectedExecutionIds.has(entry.executionId)
            ) {
                continue;
            }
            this._cancelEntry(entry, {
                code: KERNEL_RUN_CANCEL_REASON.COLLATERAL_REALM_TEARDOWN,
                triggerRunId: event.triggerRunId || null,
            }, { terminateRealm: false });
        }
    }

    _finish(entry, result) {
        if (!entry || entry.settled) return;
        entry.settled = true;
        if (entry.timer) clearTimeout(entry.timer);
        entry.timer = null;
        entry.removeExternalAbort?.();
        entry.removeExternalAbort = null;
        if (entry.launched) this._active = Math.max(0, this._active - 1);
        this._entries.delete(entry.runId);
        if (this._entries.size === 0 && this._currentExecutionId) {
            const executionId = this._currentExecutionId;
            this._currentExecutionId = null;
            // The scheduler, not an individual cell, owns the shared
            // interpreter lifetime. This is the outer completion boundary for
            // successful, failed, cancelled, and disposed canvas batches.
            try { this._releaseRealm({ executionId }); } catch { /* best effort */ }
        }

        const runOutcome = kernelRunOutcomeOf(result);
        const state = runOutcome === "cancelled"
            ? "cancelled"
            : runOutcome === "completed"
                ? "done"
                : runOutcome || "failed";
        this._emitState(entry, state, { outcome: runOutcome, result });
        const payload = {
            runId: entry.runId,
            id: entry.id,
            outcome: reportedKernelRunOutcomeOf(result),
            reasonCategory: reportedKernelRunReasonCategoryOf(result),
            runOutcome,
            analyticsQueryCount: Math.max(0, Number(result?.analyticsQueryCount) || 0),
            reason: result?.reason || null,
            result,
            metadata: entry.metadata,
        };
        try { entry.onOutcome?.(payload); } catch { /* observer only */ }
        try { this._onOutcome?.(payload); } catch { /* observer only */ }
        entry.resolve(result);
        this._pump();
    }

    _emitState(entry, state, extra = {}) {
        entry.state = state;
        try {
            entry.onStateChange?.({
                id: entry.id,
                runId: entry.runId,
                state,
                metadata: entry.metadata,
                ...extra,
            });
        } catch { /* observer only */ }
    }
}

export function createCanvasRunScheduler(options) {
    return new CanvasRunScheduler(options);
}
